import { useEffect } from 'react'
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect'
import { PartySelect } from '@/modules/registrations/contacts/PartySelect'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { LOCALE_CURRENCY } from '@/shared/components/ui/CurrencyInput'
import type { AssetHolding } from '@/modules/inventory/holdings'
import type { AssetHoldingFormData } from '@/modules/inventory/holdings/AssetHoldingListPage'
import type { AssetHoldingLocation, AssetSubgroupOption } from '@/modules/inventory/holdings'
import type { Party } from '@/modules/registrations/contacts/contactsTypes'
import type { PoultryTypeOption } from '@/modules/poultry/poultryInventoryService'

export interface BatchFormData extends AssetHoldingFormData {
  birth_date: string
  asset_subgroup_id: string
}

interface BatchFormModalProps {
  unitId: string
  formData: BatchFormData
  setFormData: (data: BatchFormData) => void
  formErrors: Record<string, string>
  submitting: boolean
  editingEntity: AssetHolding | null
  mode: 'create' | 'edit' | 'view'
  assetOptions: PoultryTypeOption[]
  assetSubgroups: AssetSubgroupOption[]
  assetsLoading: boolean
  parties: Party[]
  locations: AssetHoldingLocation[]
  onAssetCreated: (asset: any) => void
  onPartyCreated: (party: Party) => void
  onOpenAssetModal: (subgroupId?: string) => void
  onCreateLocation: () => Promise<AssetHoldingLocation | null>
}

export function BatchFormModal({
  unitId,
  formData,
  setFormData,
  formErrors,
  submitting,
  editingEntity,
  mode,
  assetOptions,
  assetSubgroups,
  assetsLoading,
  parties,
  locations,
  onAssetCreated,
  onPartyCreated,
  onOpenAssetModal,
  onCreateLocation
}: BatchFormModalProps) {
  const { t, locale } = useTranslation()

  const tc = (key: string) => t(`registrations.batches.${key}`)

  const readOnly = submitting || mode === 'view'
  const inputCls = (field: string) =>
    `w-full px-4 py-2 rounded-lg border ${
      formErrors[field] ? 'border-red-300' : 'border-slate-300'
    } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500`

  const partyOptions = [
    { value: '', label: tc('record.fields.partyNone') },
    ...parties.map((p) => ({ value: p.id, label: p.name }))
  ]
  const locationOptions = [
    { value: '', label: tc('record.fields.locationNone') },
    ...locations.map((location) => ({ value: location.id, label: location.name })),
  ]
  const subgroupOptions = [
    { value: '', label: tc('record.fields.assetSubgroupAll') },
    ...assetSubgroups.map((subgroup) => ({ value: subgroup.id, label: subgroup.name })),
  ]
  const selectedSubgroup = assetSubgroups.find((subgroup) => subgroup.id === formData.asset_subgroup_id)
  const selectedSubgroupKey = selectedSubgroup ? getPoultrySubgroupKey(selectedSubgroup.name) : formData.asset_subgroup_id
  const optionMatchesSubgroup = (option: PoultryTypeOption, subgroupId: string) => {
    if (!subgroupId) return true
    return option.subgroup_id === subgroupId || option.subgroup_key === subgroupId || option.subgroup_key === selectedSubgroupKey
  }
  const filteredAssetOptions = formData.asset_subgroup_id
    ? assetOptions.filter((option) => optionMatchesSubgroup(option, formData.asset_subgroup_id))
    : assetOptions
  const displayedAssetOptions = filteredAssetOptions.map((option) => ({
    ...option,
    meta: formData.asset_subgroup_id
      ? option.meta
      : assetSubgroups.find((subgroup) => subgroup.id === option.subgroup_id)?.name ?? option.meta,
  }))

  const handleSubgroupChange = (subgroupId: string) => {
    const selectedAsset = assetOptions.find((option) => option.value === formData.asset_id)
    const shouldClearAsset = Boolean(
      subgroupId && selectedAsset && !optionMatchesSubgroup(selectedAsset, subgroupId),
    )

    setFormData({
      ...formData,
      asset_subgroup_id: subgroupId,
      asset_id: shouldClearAsset ? '' : formData.asset_id,
    })
  }

  const handleAssetChange = (assetId: string) => {
    const selectedAsset = assetOptions.find((option) => option.value === assetId)
    const selectedSubgroupId =
      selectedAsset && subgroupOptions.some((option) => option.value === selectedAsset.subgroup_id)
        ? selectedAsset.subgroup_id
        : selectedAsset?.subgroup_key

    setFormData({
      ...formData,
      asset_id: assetId,
      asset_subgroup_id: selectedSubgroupId || '',
    })
  }

  useEffect(() => {
    if (!formData.asset_id || formData.asset_subgroup_id) return

    const selectedAsset = assetOptions.find((option) => option.value === formData.asset_id)
    if (!selectedAsset?.subgroup_id) return

    setFormData({
      ...formData,
      asset_subgroup_id: selectedAsset.subgroup_id,
    })
  }, [assetOptions, formData, setFormData])

  return (
    <div className="space-y-4">
      {/* Asset (tipo de ave) — somente na criação */}
      {mode === 'create' && (
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('record.fields.assetSubgroup')}
            </label>
            <SearchableSelect
              options={subgroupOptions}
              value={formData.asset_subgroup_id}
              onChange={handleSubgroupChange}
              placeholder={tc('record.fields.assetSubgroupPlaceholder')}
              disabled={readOnly || assetsLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('record.fields.assetType')} <span className="text-red-500">*</span>
            </label>
            {assetsLoading ? (
              <div className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-500 flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-slate-600 rounded-full" />
                {tc('record.fields.assetTypeLoading')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <SearchableSelect
                    options={displayedAssetOptions}
                    value={formData.asset_id}
                    onChange={handleAssetChange}
                    placeholder={tc('record.fields.assetTypePlaceholder')}
                    disabled={readOnly}
                    error={!!formErrors.asset_id}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onOpenAssetModal(formData.asset_subgroup_id || undefined)}
                  disabled={readOnly}
                  title={tc('actions.newAssetType')}
                  className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 hover:border-emerald-400 hover:text-emerald-600 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            )}
            {formErrors.asset_id && <p className="mt-1 text-sm text-red-600">{formErrors.asset_id}</p>}
          </div>
        </div>
      )}

      {/* Nome do lote */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tc('record.fields.name')} <span className="text-slate-400 font-normal">{tc('record.fields.nameOptional')}</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tc('record.fields.namePlaceholder')}
          className={inputCls('name')}
          disabled={readOnly}
          autoFocus={mode !== 'create'}
        />
      </div>

      {/* Quantidade inicial + Custo unitário + Custo total — somente na criação */}
      {mode === 'create' && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('record.fields.quantity')} <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.initial_quantity}
              onChange={(e) => setFormData({ ...formData, initial_quantity: e.target.value })}
              placeholder={tc('record.fields.quantityPlaceholder')}
              className={inputCls('initial_quantity')}
              disabled={readOnly}
            />
            {formErrors.initial_quantity && (
              <p className="mt-1 text-sm text-red-600">{formErrors.initial_quantity}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('record.fields.costPerUnit')} <span className="text-slate-400 font-normal">{tc('record.fields.totalCostOptional')}</span>
            </label>
            {(() => {
              const qty = parseFloat(formData.initial_quantity || '0')
              const tot = parseFloat(formData.total_cost || '0')
              if (!isNaN(tot) && tot > 0 && qty > 0) {
                const per = tot / qty
                const currency = LOCALE_CURRENCY[locale] ?? 'BRL'
                return (
                  <div className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-600 text-slate-900 dark:text-white">
                    {new Intl.NumberFormat(locale, { style: 'currency', currency }).format(per)}
                  </div>
                )
              }
              return (
                <div className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-600 text-slate-400 dark:text-slate-400">
                  —
                </div>
              )
            })()}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('record.fields.totalCost')} <span className="text-slate-400 font-normal">{tc('record.fields.totalCostOptional')}</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.total_cost}
              onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
              placeholder={tc('record.fields.totalCostPlaceholder')}
              className={inputCls('total_cost')}
              disabled={readOnly}
            />
            {formErrors.total_cost && (
              <p className="mt-1 text-sm text-red-600">{formErrors.total_cost}</p>
            )}
          </div>
        </div>
      )}

     {/* Fornecedor/Origem */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tc('record.fields.party')} <span className="text-slate-400 font-normal">{tc('record.fields.nameOptional')}</span>
        </label>
        <PartySelect
          unitId={unitId}
          options={partyOptions}
          value={formData.party_id}
          onChange={(v) => setFormData({ ...formData, party_id: v })}
          onPartyCreated={onPartyCreated}
          placeholder={tc('record.fields.partyPlaceholder')}
          disabled={readOnly}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tc('record.fields.location')} <span className="text-slate-400 font-normal">{tc('record.fields.nameOptional')}</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <SearchableSelect
              options={locationOptions}
              value={formData.location_id}
              onChange={(v) => setFormData({ ...formData, location_id: v })}
              placeholder={tc('record.fields.locationPlaceholder')}
              disabled={readOnly || mode !== 'create'}
            />
          </div>
          {mode === 'create' && (
            <button
              type="button"
              onClick={async () => {
                const created = await onCreateLocation()
                if (created) setFormData({ ...formData, location_id: created.id })
              }}
              disabled={readOnly}
              title={tc('actions.newLocation')}
              className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 hover:border-emerald-400 hover:text-emerald-600 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Datas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {tc('record.fields.acquiredAt')}
          </label>
          <input
            type="date"
            value={formData.acquired_at}
            onChange={(e) => setFormData({ ...formData, acquired_at: e.target.value })}
            className={inputCls('acquired_at')}
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {tc('record.fields.startedAt')}
          </label>
          <input
            type="date"
            value={formData.started_at}
            onChange={(e) => setFormData({ ...formData, started_at: e.target.value })}
            className={inputCls('started_at')}
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {tc('record.fields.birthDate')}
          </label>
          <input
            type="date"
            value={formData.birth_date}
            onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
            className={inputCls('birth_date')}
            disabled={readOnly}
          />
          {formErrors.birth_date && (
            <p className="mt-1 text-sm text-red-600">{formErrors.birth_date}</p>
          )}
        </div>
      </div>

     </div>
  )
}

function getPoultrySubgroupKey(name: string): string {
  const normalizedName = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()

  const keysByName: Record<string, string> = {
    galinhas: 'poultry:chickens',
    patos: 'poultry:ducks',
    gansos: 'poultry:geese',
    perus: 'poultry:turkeys',
    codornas: 'poultry:quails',
    capotes: 'poultry:guinea-fowl',
    psitacideos: 'poultry:psittacines',
    'outras aves': 'poultry:other-birds',
  }

  return keysByName[normalizedName] ?? normalizedName
}
